import { useState, FormEvent } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import Button from '../components/shared/Button'
import Input from '../components/shared/Input'
import Alert from '../components/shared/Alert'
import Card from '../components/shared/Card'
import { validateEmail, shouldShowError } from '../utils/validation'
import logoSvg from '../assets/logo.svg'
import './Login.css'

export default function Login() {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | undefined>(undefined)
  const [emailTouched, setEmailTouched] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const { login } = useAuth()
  const location = useLocation()

  // Check if redirected from dashboard
  const fromDashboard = location.state?.from?.pathname?.startsWith('/dashboard')

  const handleEmailChange = (value: string) => {
    setEmail(value)
    // Validate on change after first touch
    if (emailTouched) {
      const result = validateEmail(value)
      setEmailError(result.error)
    }
  }

  const handleEmailBlur = () => {
    setEmailTouched(true)
    const result = validateEmail(email)
    setEmailError(result.error)
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitted(true)

    // Validate email
    const emailResult = validateEmail(email)
    if (!emailResult.isValid) {
      setEmailError(emailResult.error)
      return
    }

    setIsSubmitting(true)

    const result = await login(email)

    if (result.success) {
      setIsSuccess(true)
    } else {
      setError(result.error || 'Something went wrong. Please try again.')
    }

    setIsSubmitting(false)
  }

  // Success state - email sent
  if (isSuccess) {
    return (
      <div className="login-page">
        <Link to="/" className="login-logo">
          <img src={logoSvg} alt="PlanScan" />
        </Link>

        <Card className="login-card">
          <div className="login-success">
            <div className="login-success-icon">✓</div>
            <h1 className="login-title">Check Your Email</h1>
            <p className="login-subtitle">
              We sent a sign-in link to <strong>{email}</strong>
            </p>
            <p className="login-help">
              Click the link in the email to sign in. The link expires in 15 minutes.
            </p>
            <Button
              variant="secondary"
              onClick={() => {
                setIsSuccess(false)
                setEmail('')
              }}
            >
              Use a Different Email
            </Button>
          </div>
        </Card>

        <p className="login-footer">
          Didn't receive an email?{' '}
          <button
            type="button"
            className="login-link"
            onClick={() => {
              setIsSuccess(false)
            }}
          >
            Try again
          </button>
        </p>
      </div>
    )
  }

  return (
    <div className="login-page">
      <Link to="/" className="login-logo">
        <img src={logoSvg} alt="PlanScan" />
      </Link>

      <Card className="login-card">
        <h1 className="login-title">Sign In to PlanScan</h1>
        <p className="login-subtitle">
          Enter your email and we'll send you a secure sign-in link.
          No password needed.
        </p>

        {fromDashboard && !error && (
          <Alert variant="info" className="login-alert">
            Please sign in to access your dashboard.
          </Alert>
        )}

        {error && (
          <Alert variant="danger" className="login-alert" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <Input
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => handleEmailChange(e.target.value)}
            onBlur={handleEmailBlur}
            placeholder="you@company.com"
            required
            autoComplete="email"
            autoFocus
            fullWidth
            error={shouldShowError(emailTouched, submitted, emailError)}
          />

          <Button
            type="submit"
            variant="primary"
            size="large"
            loading={isSubmitting}
            fullWidth
          >
            Send Sign-In Link
          </Button>
        </form>
      </Card>

      <p className="login-footer">
        Don't have an account?{' '}
        <Link to="/download" className="login-link">
          Start your free trial
        </Link>
      </p>
    </div>
  )
}
